import { Schema } from "effect";

import type {
  ControllerKind,
  ControllerName,
  KubeSystemUid,
  NamespaceName,
} from "../k8s/ids";

/**
 * Unified fingerprint format for issue deduplication.
 *
 * Format: {source}:{sourceId}:{scope}:{resource}:{issueType}
 *
 * - source: "k8s" | "azure-devops"
 * - sourceId: clusterId or connectionId
 * - scope: namespace or projectName
 * - resource: podName or pipelineId
 * - issueType: cpu_threshold_exceeded, pipeline_failed, etc.
 */

/**
 * Branded type for fingerprint strings
 */
export const Fingerprint = Schema.String.pipe(
  Schema.brand("Fingerprint")
);
export type Fingerprint = typeof Fingerprint.Type;

/**
 * Branded type for fingerprint prefix (for LIKE queries)
 */
export const FingerprintPrefix = Schema.String.pipe(
  Schema.brand("FingerprintPrefix")
);
export type FingerprintPrefix =
  typeof FingerprintPrefix.Type;

export const FingerprintSources = {
  AzureDevOps: "azure-devops",
  GitHubActions: "github-actions",
  K8s: "k8s", // Legacy - kept for migration, do not use for new fingerprints
  K8sIssues: "k8s-issues", // Realtime K8s issues agent
  K8sOpt: "k8s-opt", // K8s optimization agent (daily Prometheus analysis)
  OtelReceiver: "otel",
  Sentry: "sentry",
} as const;

export type K8sFingerprintSource =
  | typeof FingerprintSources.K8s
  | typeof FingerprintSources.K8sIssues
  | typeof FingerprintSources.K8sOpt;

export type FingerprintSource =
  (typeof FingerprintSources)[keyof typeof FingerprintSources];

/**
 * Issue type constants for K8s metrics agent
 */
export const K8sIssueTypes = {
  CpuThresholdExceeded: "cpu_threshold_exceeded",
  HighRestartCount: "high_restart_count",
  JobFailed: "job_failed",
  MemoryThresholdExceeded: "memory_threshold_exceeded",
  NoResourceLimits: "no_resource_limits",
  OverprovisionedCpu: "overprovisioned_cpu",
  OverprovisionedMemory: "overprovisioned_memory",
  PodCrashLoopBackOff: "pod_crash_loop_backoff",
  PodNotReady: "pod_not_ready",
  PvcCapacityCritical: "pvc_capacity_critical",
  PvcCapacityWarning: "pvc_capacity_warning",
  PvcInodeCritical: "pvc_inode_critical",
  PvcInodeWarning: "pvc_inode_warning",
  ResourceQuotaExceeded: "resource_quota_exceeded",
  Unknown: "unknown",
} as const;

export type K8sIssueType =
  (typeof K8sIssueTypes)[keyof typeof K8sIssueTypes];

/**
 * K8s issue types that can be automatically closed when resolved.
 *
 * These are "persistent" issues that indicate a clear problem-to-resolution cycle:
 * - CrashLoopBackOff → Pod recovered/restarted successfully
 * - HighRestartCount → Restart count dropped below threshold
 * - NoResourceLimits → Limits were configured
 * - JobFailed → Job was deleted/succeeded on retry
 * - ResourceQuotaExceeded → Quota was adjusted/usage decreased
 *
 * NOT auto-closable (transient spikes that shouldn't auto-close):
 * - CpuThresholdExceeded - CPU spikes are often temporary, closing masks patterns
 * - MemoryThresholdExceeded - Memory spikes may indicate leaks, needs investigation
 * - PodNotReady - Can flip-flop, should be investigated
 * - Unknown - Can't determine nature, safer to keep open
 */
export const AUTO_CLOSABLE_K8S_ISSUE_TYPES: ReadonlySet<K8sIssueType> =
  new Set([
    K8sIssueTypes.HighRestartCount,
    K8sIssueTypes.JobFailed,
    K8sIssueTypes.NoResourceLimits,
    K8sIssueTypes.PodCrashLoopBackOff,
    K8sIssueTypes.ResourceQuotaExceeded,
  ]);

/**
 * K8s issue types that indicate a FAILURE condition.
 *
 * These issues should NOT be auto-closed when the workload is deleted,
 * because the deletion means Kubernetes cleaned up after a failure,
 * not that the problem was resolved.
 *
 * Example: A backup CronJob fails with CrashLoopBackOff. Kubernetes
 * deletes the failed Job pod after backoff limit. The issue should
 * remain open for investigation - the backup didn't succeed.
 */
export const FAILURE_K8S_ISSUE_TYPES: ReadonlySet<K8sIssueType> =
  new Set([
    K8sIssueTypes.HighRestartCount,
    K8sIssueTypes.JobFailed,
    K8sIssueTypes.PodCrashLoopBackOff,
  ]);

/**
 * Issue type constants for Azure DevOps pipeline agent
 */
export const AzureDevOpsIssueTypes = {
  PipelineFailed: "pipeline_failed",
} as const;

export type AzureDevOpsIssueType =
  (typeof AzureDevOpsIssueTypes)[keyof typeof AzureDevOpsIssueTypes];

/**
 * Issue type constants for GitHub Actions workflow agent
 */
export const GitHubActionsIssueTypes = {
  WorkflowFailed: "workflow_failed",
  WorkflowStuckWaitingForRunner:
    "workflow_stuck_waiting_for_runner",
} as const;

export type GitHubActionsIssueType =
  (typeof GitHubActionsIssueTypes)[keyof typeof GitHubActionsIssueTypes];

export const WORKFLOW_FAILED_RESOURCE_SEPARATOR = "_";
export const STUCK_RUNNER_RESOURCE_SUFFIX =
  "_runner_waiting";

/**
 * Issue type constants for Sentry agent
 */
export const SentryIssueTypes = {
  NewError: "new_error",
  PerformanceIssue: "performance_issue",
  Regression: "regression",
} as const;

export type SentryIssueType =
  (typeof SentryIssueTypes)[keyof typeof SentryIssueTypes];

export const OtelReceiverIssueTypes = {
  Error: "error",
} as const;

export type OtelReceiverIssueType =
  (typeof OtelReceiverIssueTypes)[keyof typeof OtelReceiverIssueTypes];

export type IssueType =
  | AzureDevOpsIssueType
  | GitHubActionsIssueType
  | K8sIssueType
  | OtelReceiverIssueType
  | SentryIssueType;

/**
 * Parameters for generating a fingerprint
 */
export type FingerprintParams = {
  issueType: IssueType;
  resource: string;
  scope: string;
  source: FingerprintSource;
  sourceId: string;
};

/**
 * Parsed fingerprint components
 */
export type ParsedFingerprint = {
  issueType: string;
  resource: string;
  scope: string;
  source: FingerprintSource;
  sourceId: string;
};

/**
 * K8s-specific fingerprint parameters
 */
export type K8sFingerprintParams = {
  /** Controller kind (ReplicaSet, DaemonSet, StatefulSet, Job, etc.) - used for v2 fingerprint logic */
  controllerKind?: ControllerKind | null;
  /** Controller name for stable fingerprinting (used instead of resource if provided) */
  controllerName?: ControllerName | null;
  issueType: K8sIssueType;
  /** Kubernetes kube-system namespace UID - used as stable cluster identifier */
  kubeSystemUid: KubeSystemUid;
  namespace: NamespaceName;
  resource: string;
  /** K8s agent source - REQUIRED to ensure proper fingerprint isolation between agents */
  source: K8sFingerprintSource;
};

/**
 * Azure DevOps-specific fingerprint parameters
 */
export type AzureDevOpsFingerprintParams = {
  connectionId: string;
  /** Error message for stable fingerprinting - same errors get same fingerprint */
  errorMessage?: string;
  pipelineId: number;
  projectName: string;
  /** Run ID as fallback when errorMessage is unavailable - ensures unique fingerprints */
  runId?: number;
};

/**
 * GitHub Actions-specific fingerprint parameters
 */
export type GitHubActionsFingerprintParams = {
  installationId: string;
  owner: string;
  repo: string;
  workflowId: number;
  headBranch: string;
  /** Error message for stable fingerprinting - same errors get same fingerprint */
  errorMessage?: string;
  /** Run ID as fallback when errorMessage is unavailable - ensures unique fingerprints */
  runId?: number;
};

export type GitHubActionsStuckRunnerFingerprintParams = {
  installationId: string;
  owner: string;
  repo: string;
  workflowId: number;
  headBranch: string;
};

/**
 * Sentry-specific fingerprint parameters
 */
export type SentryFingerprintParams = {
  orgSlug: string;
  projectSlug: string;
  sentryIssueId: string;
};

/**
 * Alias for backward compatibility - same as Fingerprint string
 */
export type IssueFingerprint = string;

/**
 * Normalize a string for fingerprint consistency.
 * Lowercases, trims, and replaces special characters.
 */
export function normalizeForFingerprint(
  value: string
): string {
  return value.toLowerCase().trim().replace(/:/g, "_");
}

/**
 * Extract a stable prefix from a pod/resource name for fingerprinting.
 * Removes dynamic suffixes like UUIDs, hex hashes, and random K8s pod suffixes.
 * Only removes suffixes that appear to be auto-generated (contain digits).
 *
 * Examples:
 * - "share-manager-pvc-7dc89c7f-c9ac-4c6b-a635-8cf52207cf02" → "share-manager-pvc"
 * - "instance-manager-5f80cf38a34403fc923970112b882db6" → "instance-manager"
 * - "longhorn-csi-plugin-2s8r5" → "longhorn-csi-plugin"
 * - "api-server" → "api-server" (no change - no random suffix)
 * - "web-app-787cf77cdc-w6glv" → "web-app-787cf77cdc" (keeps ReplicaSet hash)
 * - "db-backup-tools-db-backups-backup-29499710" → "db-backup-tools-db-backups-backup" (CronJob timestamp)
 */
export function extractStablePodPrefix(
  resourceName: string
): string {
  let result = resourceName;

  // Remove full UUIDs (8-4-4-4-12 format)
  // e.g., "share-manager-pvc-7dc89c7f-c9ac-4c6b-a635-8cf52207cf02" → "share-manager-pvc"
  result = result.replace(
    /-?[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i,
    ""
  );

  // Remove long hex hashes (20+ characters)
  // e.g., "instance-manager-5f80cf38a34403fc923970112b882db6" → "instance-manager"
  result = result.replace(/-[a-f0-9]{20,}$/i, "");

  // Pattern 1: ReplicaSet/DaemonSet pods with format {name}-{8-10-char-hash}-{5-char-suffix}
  // K8s hashes vary in length (8-10 alphanumeric chars)
  // e.g., "web-app-787cf77cdc-w6glv" → "web-app-787cf77cdc"
  // e.g., "paidadvisory-api-68b8d65bc-knspk" → "paidadvisory-api-68b8d65bc"
  // e.g., "engine-image-ei-c2d50bcc-lzcnt" → "engine-image-ei-c2d50bcc"
  result = result.replace(
    /-([a-z0-9]{8,10})-[a-z0-9]{5}$/,
    (_, hash) => `-${hash}`
  );

  // Pattern 2: DaemonSet/standalone pods with 5-char suffix containing at least one digit
  // e.g., "longhorn-csi-plugin-2s8r5" → "longhorn-csi-plugin"
  // e.g., "longhorn-manager-rnk9j" → "longhorn-manager"
  // But keeps: "cache-redis" (no digits in -redis)
  // Pattern: dash + 5 chars that contain at least one digit
  result = result.replace(/-(?=.*\d)[a-z0-9]{5}$/i, "");

  // Pattern 3: CronJob-spawned Jobs with numeric timestamp suffix (7+ digits)
  // e.g., "db-backup-tools-db-backups-backup-29499710" → "db-backup-tools-db-backups-backup"
  // e.g., "cleanup-job-29501246" → "cleanup-job"
  // CronJob timestamps are Unix minutes (currently 8 digits, will grow to 9 digits around 2037)
  result = result.replace(/-\d{7,}$/, "");

  // Clean up trailing dashes
  result = result.replace(/-+$/, "");

  // If we stripped everything, return original
  return result || resourceName;
}

/**
 * Generate a unified fingerprint string.
 *
 * @example
 * generateFingerprint({
 *   source: "k8s",
 *   sourceId: "clstr_abc",
 *   scope: "vivus-prod",
 *   resource: "api-server",
 *   issueType: "cpu_threshold_exceeded"
 * })
 * // Returns: "k8s:clstr_abc:vivus-prod:api-server:cpu_threshold_exceeded"
 */
export function generateFingerprint(
  params: FingerprintParams
): Fingerprint {
  const { issueType, resource, scope, source, sourceId } =
    params;

  const normalizedScope = normalizeForFingerprint(scope);
  const normalizedResource =
    normalizeForFingerprint(resource);

  return Fingerprint.make(
    `${source}:${sourceId}:${normalizedScope}:${normalizedResource}:${issueType}`
  );
}

/**
 * Standard K8s controller kinds that have stable names.
 * These controllers use consistent naming and don't need prefix extraction.
 *
 * Note: ReplicaSet is NOT included because ReplicaSet names contain deployment hash
 * (e.g., "web-app-787cf77cdc") which changes with each deployment.
 * ReplicaSets are resolved to their parent Deployment in K8s service processing.
 *
 * Note: Job is NOT included because CronJob-spawned Jobs contain timestamps
 * (e.g., "backup-job-29499710") which change with each CronJob run.
 */
const STABLE_CONTROLLER_KINDS = [
  "CronJob",
  "DaemonSet",
  "Deployment",
  "StatefulSet",
] as const;

/**
 * Generate a K8s-specific fingerprint.
 *
 * For standard controller kinds (ReplicaSet, DaemonSet, etc.), uses the exact
 * controller name without extraction. This produces stable fingerprints.
 *
 * For unknown controller kinds or standalone pods, falls back to
 * extractStablePodPrefix() for safety.
 *
 * @example
 * // Standard controller - uses exact name
 * generateK8sFingerprint({
 *   kubeSystemUid: "clstr_abc",
 *   namespace: "vivus-prod",
 *   resource: "api-server-787cf77cdc-w6glv",
 *   controllerName: "api-server-787cf77cdc",
 *   controllerKind: "ReplicaSet",
 *   issueType: "cpu_threshold_exceeded"
 * })
 * // Returns: "k8s:clstr_abc:vivus-prod:api-server-787cf77cdc:cpu_threshold_exceeded"
 *
 * @example
 * // Unknown controller kind - applies extraction
 * generateK8sFingerprint({
 *   kubeSystemUid: "clstr_abc",
 *   namespace: "longhorn-system",
 *   resource: "share-manager-pvc-7dc89c7f-c9ac-4c6b-a635-8cf52207cf02",
 *   controllerName: "pvc-7dc89c7f-c9ac-4c6b-a635-8cf52207cf02",
 *   controllerKind: null,
 *   issueType: "no_resource_limits"
 * })
 * // Returns: "k8s:clstr_abc:longhorn-system:pvc:no_resource_limits"
 */
export function generateK8sFingerprint(
  params: K8sFingerprintParams
): Fingerprint {
  let stableResource: string;

  if (params.controllerName) {
    // Check if controller kind is standard (has stable name)
    const isStableKind =
      params.controllerKind &&
      STABLE_CONTROLLER_KINDS.includes(
        params.controllerKind as (typeof STABLE_CONTROLLER_KINDS)[number]
      );

    if (isStableKind) {
      // Use exact controller name - no extraction needed
      stableResource = params.controllerName;
    } else {
      // Unknown controller kind - apply extraction for safety
      stableResource = extractStablePodPrefix(
        params.controllerName
      );
    }
  } else {
    // No controller - extract from pod name
    stableResource = extractStablePodPrefix(
      params.resource
    );
  }

  return generateFingerprint({
    issueType: params.issueType,
    resource: stableResource,
    scope: params.namespace,
    source: params.source,
    sourceId: params.kubeSystemUid,
  });
}

/**
 * Hash an error message for stable fingerprinting.
 * Normalizes dynamic values (numbers, paths, pod suffixes) and extracts key words.
 *
 * @example
 * hashErrorMessage("Error: UPGRADE FAILED: context deadline exceeded")
 * // Returns: "upgrade_context_deadline_751e0"
 */
export function hashErrorMessage(message: string): string {
  let normalized = message.toLowerCase().trim();

  // Remove dynamic parts that shouldn't affect deduplication
  normalized = normalized
    .replace(/\d+\.\d+/g, "X") // decimal numbers (3.694 → X)
    .replace(/\b[a-f0-9]{5,}\b/g, "X") // hex strings (pod suffixes like 787cf77cdc)
    .replace(/\b[a-z0-9]{5}-[a-z0-9]{5}\b/g, "X") // k8s style suffixes (w6glv-abc12)
    .replace(/-[a-z0-9]{4,10}(?=\s|$|")/g, "-X") // trailing pod/container suffixes
    .replace(/\d+/g, "X") // remaining integers
    .replace(/\/[\w/-]+/g, "/path") // paths like /usr/bin/docker → /path
    .replace(/"[^"]+"/g, "X") // quoted strings (release names like "web-app")
    .replace(/'[^']+'/g, "X") // single-quoted strings
    .replace(/["'`]/g, "") // remaining quotes
    .replace(/\s+/g, " "); // normalize whitespace

  const skipWords = new Set([
    "a",
    "an",
    "and",
    "at",
    "code",
    "error",
    "failed",
    "for",
    "in",
    "is",
    "of",
    "on",
    "path",
    "the",
    "to",
    "with",
    "x",
  ]);

  // Extract meaningful words (max 3 to leave room for hash suffix)
  const words = normalized
    .split(/[\s:]+/)
    .map((w) => w.replace(/[^a-z]/g, ""))
    .filter((w) => w.length > 2 && !skipWords.has(w))
    .slice(0, 3)
    .join("_");

  // Create short hash of normalized message for uniqueness
  // Use simple string hash since crypto may not be available in all environments
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  const hashHex = Math.abs(hash).toString(16).slice(0, 6);
  // Truncate prefix to leave room for _hash (7 chars)
  const prefix = (words || "error").slice(0, 24);

  return `${prefix}_${hashHex}`;
}

/**
 * Generate an Azure DevOps-specific fingerprint.
 * Uses error message hash for stable deduplication - same errors get same fingerprint.
 *
 * @example
 * generateAzureDevOpsFingerprint({
 *   connectionId: "conn_123",
 *   projectName: "my-project",
 *   pipelineId: 456,
 *   errorMessage: "UPGRADE FAILED: context deadline exceeded"
 * })
 * // Returns: "azure-devops:conn_123:my-project:456_upgrade_context_deadline_751e0:pipeline_failed"
 */
export function generateAzureDevOpsFingerprint(
  params: AzureDevOpsFingerprintParams
): Fingerprint {
  // Use error message hash for stable fingerprinting
  // Fall back to runId if errorMessage unavailable (prevents incorrect deduplication)
  // Only use "unknown" as last resort (when neither errorMessage nor runId available)
  let errorHash: string;
  if (params.errorMessage) {
    errorHash = hashErrorMessage(params.errorMessage);
  } else if (params.runId !== undefined) {
    errorHash = `run_${params.runId}`;
  } else {
    errorHash = "unknown";
  }

  const resource = `${params.pipelineId}_${errorHash}`;

  return generateFingerprint({
    issueType: AzureDevOpsIssueTypes.PipelineFailed,
    resource,
    scope: params.projectName,
    source: FingerprintSources.AzureDevOps,
    sourceId: params.connectionId,
  });
}

/**
 * Generate a GitHub Actions-specific fingerprint.
 * Uses error message hash for stable deduplication.
 * Branch is part of the fingerprint — same workflow on different branches produces different fingerprints.
 *
 * @example
 * generateGitHubActionsFingerprint({
 *   installationId: "inst_123",
 *   owner: "acme",
 *   repo: "api",
 *   workflowId: 456,
 *   headBranch: "main",
 *   errorMessage: "npm test failed: 3 tests failed"
 * })
 * // Returns: "github-actions:inst_123:acme/api:456_main_npm_tests_abc12:workflow_failed"
 */
export function generateGitHubActionsFingerprint(
  params: GitHubActionsFingerprintParams
): Fingerprint {
  let errorHash: string;
  if (params.errorMessage) {
    errorHash = hashErrorMessage(params.errorMessage);
  } else if (params.runId !== undefined) {
    errorHash = `run_${params.runId}`;
  } else {
    errorHash = "unknown";
  }

  // Include branch in resource for per-branch deduplication
  const resource = `${params.workflowId}_${params.headBranch}${WORKFLOW_FAILED_RESOURCE_SEPARATOR}${errorHash}`;

  return generateFingerprint({
    issueType: GitHubActionsIssueTypes.WorkflowFailed,
    resource,
    scope: `${params.owner}/${params.repo}`,
    source: FingerprintSources.GitHubActions,
    sourceId: params.installationId,
  });
}

export function generateGitHubActionsStuckRunnerFingerprint(
  params: GitHubActionsStuckRunnerFingerprintParams
): Fingerprint {
  const resource = `${params.workflowId}_${params.headBranch}${STUCK_RUNNER_RESOURCE_SUFFIX}`;

  return generateFingerprint({
    issueType:
      GitHubActionsIssueTypes.WorkflowStuckWaitingForRunner,
    resource,
    scope: `${params.owner}/${params.repo}`,
    source: FingerprintSources.GitHubActions,
    sourceId: params.installationId,
  });
}

/**
 * Parse a fingerprint string back into its components.
 * Returns null if the fingerprint is invalid.
 *
 * @example
 * parseFingerprint("k8s:clstr_abc:vivus-prod:api-server:cpu_threshold_exceeded")
 * // Returns: { source: "k8s", sourceId: "clstr_abc", scope: "vivus-prod", resource: "api-server", issueType: "cpu_threshold_exceeded" }
 */
export function parseFingerprint(
  fingerprint: string
): ParsedFingerprint | null {
  const parts = fingerprint.split(":");

  if (parts.length < 5) {
    return null;
  }

  const [
    source,
    sourceId,
    scope,
    resource,
    ...issueTypeParts
  ] = parts;

  if (!source || !sourceId || !scope || !resource) {
    return null;
  }

  if (
    source !== FingerprintSources.K8s &&
    source !== FingerprintSources.K8sIssues &&
    source !== FingerprintSources.K8sOpt &&
    source !== FingerprintSources.AzureDevOps &&
    source !== FingerprintSources.GitHubActions &&
    source !== FingerprintSources.OtelReceiver &&
    source !== FingerprintSources.Sentry
  ) {
    return null;
  }

  // issueType may contain colons (e.g., for future extensions), join remaining parts
  const issueType = issueTypeParts.join(":");

  if (!issueType) {
    return null;
  }

  return {
    issueType,
    resource,
    scope,
    source: source as FingerprintSource,
    sourceId,
  };
}

/**
 * Check if a fingerprint belongs to any K8s source (issues or optimization).
 */
export function isK8sFingerprint(
  fingerprint: string
): boolean {
  return (
    fingerprint.startsWith(
      `${FingerprintSources.K8sIssues}:`
    ) ||
    fingerprint.startsWith(`${FingerprintSources.K8sOpt}:`)
  );
}

/**
 * K8s issue types that belong to the optimization agent.
 */
export const OPTIMIZATION_K8S_ISSUE_TYPES: ReadonlySet<K8sIssueType> =
  new Set([
    K8sIssueTypes.OverprovisionedCpu,
    K8sIssueTypes.OverprovisionedMemory,
  ]);

/**
 * Determine the correct K8s source based on issue type.
 */
export function getK8sSourceForIssueType(
  issueType: K8sIssueType
): K8sFingerprintSource {
  if (OPTIMIZATION_K8S_ISSUE_TYPES.has(issueType)) {
    return FingerprintSources.K8sOpt;
  }
  return FingerprintSources.K8sIssues;
}

/**
 * Check if a fingerprint belongs to Azure DevOps source.
 */
export function isAzureDevOpsFingerprint(
  fingerprint: string
): boolean {
  return fingerprint.startsWith(
    `${FingerprintSources.AzureDevOps}:`
  );
}

/**
 * Check if a fingerprint belongs to Sentry source.
 */
export function isSentryFingerprint(
  fingerprint: string
): boolean {
  return fingerprint.startsWith(
    `${FingerprintSources.Sentry}:`
  );
}

/**
 * Generate a Sentry-specific fingerprint.
 *
 * Format: sentry:{orgSlug}:{projectSlug}:{sentryIssueId}
 *
 * @example
 * generateSentryFingerprint({
 *   orgSlug: "blogic",
 *   projectSlug: "vivus-web",
 *   sentryIssueId: "123456789"
 * })
 * // Returns: "sentry:blogic:vivus-web:123456789:new_error"
 */
export function generateSentryFingerprint(
  params: SentryFingerprintParams
): Fingerprint {
  return generateFingerprint({
    issueType: SentryIssueTypes.NewError,
    resource: params.sentryIssueId,
    scope: params.projectSlug,
    source: FingerprintSources.Sentry,
    sourceId: params.orgSlug,
  });
}

/**
 * Parse a Sentry fingerprint string back into its components.
 * Returns null if the fingerprint is invalid or not a Sentry fingerprint.
 */
export function parseSentryFingerprint(
  fingerprint: string
): SentryFingerprintParams | null {
  if (!isSentryFingerprint(fingerprint)) {
    return null;
  }

  const parsed = parseFingerprint(fingerprint);
  if (!parsed) {
    return null;
  }

  return {
    orgSlug: parsed.sourceId,
    projectSlug: parsed.scope,
    sentryIssueId: parsed.resource,
  };
}

// --- OTel Receiver fingerprinting ---

export type OtelFingerprintParams = {
  agentId: string;
  errorMessage: string;
  serviceName: string;
};

export type ParsedOtelFingerprint = {
  agentId: string;
  errorHash: string;
  serviceName: string;
};

export function normalizeOtelErrorMessage(
  message: string
): string {
  return message
    .replace(
      /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi,
      "<UUID>"
    )
    .replace(
      /\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}[.\dZ]*/g,
      "<TIMESTAMP>"
    )
    .replace(/0x[a-f0-9]+/gi, "<ADDR>")
    .replace(/:[0-9]+/g, ":<LINE>")
    .replace(/request[_-]?id[=: ]+\S+/gi, "request_id=<ID>")
    .replace(/\b[a-f0-9]{24,}\b/gi, "<HEX>")
    .trim();
}

export function generateOtelFingerprint(
  params: OtelFingerprintParams
): Fingerprint {
  const normalized = normalizeOtelErrorMessage(
    params.errorMessage
  );
  const errorHash = hashErrorMessage(normalized);

  return generateFingerprint({
    issueType: OtelReceiverIssueTypes.Error,
    resource: errorHash,
    scope: params.serviceName || "unknown-service",
    source: FingerprintSources.OtelReceiver,
    sourceId: params.agentId,
  });
}

export function isOtelFingerprint(
  fingerprint: string
): boolean {
  return fingerprint.startsWith(
    `${FingerprintSources.OtelReceiver}:`
  );
}

export function parseOtelFingerprint(
  fingerprint: string
): ParsedOtelFingerprint | null {
  if (!isOtelFingerprint(fingerprint)) {
    return null;
  }

  const parsed = parseFingerprint(fingerprint);
  if (!parsed) {
    return null;
  }

  return {
    agentId: parsed.sourceId,
    errorHash: parsed.resource,
    serviceName: parsed.scope,
  };
}

/**
 * Detect K8s issue type from a message string.
 */
export function detectK8sIssueType(
  message: string
): K8sIssueType {
  const lowerMessage = message.toLowerCase();

  // Check for resource limits issues first (more specific)
  if (
    lowerMessage.includes("no resource limits") ||
    lowerMessage.includes("no cpu limit") ||
    lowerMessage.includes("no memory limit")
  ) {
    return K8sIssueTypes.NoResourceLimits;
  }

  // Check for over-provisioned issues (optimization agent)
  if (
    lowerMessage.includes("over-provisioned") ||
    lowerMessage.includes("overprovisioned")
  ) {
    if (lowerMessage.includes("cpu")) {
      return K8sIssueTypes.OverprovisionedCpu;
    }
    if (lowerMessage.includes("memory")) {
      return K8sIssueTypes.OverprovisionedMemory;
    }
  }

  if (lowerMessage.includes("cpu")) {
    return K8sIssueTypes.CpuThresholdExceeded;
  }

  if (lowerMessage.includes("memory")) {
    return K8sIssueTypes.MemoryThresholdExceeded;
  }

  if (lowerMessage.includes("restart")) {
    return K8sIssueTypes.HighRestartCount;
  }

  if (
    lowerMessage.includes("crashloop") ||
    lowerMessage.includes("crash loop")
  ) {
    return K8sIssueTypes.PodCrashLoopBackOff;
  }

  if (
    lowerMessage.includes("not ready") ||
    lowerMessage.includes("notready") ||
    lowerMessage.includes("unhealthy")
  ) {
    return K8sIssueTypes.PodNotReady;
  }

  if (
    lowerMessage.includes("quota") &&
    lowerMessage.includes("exceeded")
  ) {
    return K8sIssueTypes.ResourceQuotaExceeded;
  }

  // Check for job/pod failures (exit codes, terminated errors, OOMKilled)
  // Use regex for "exit code N" to be more specific and avoid false positives
  // Matches: "exit code 1", "exit code: 1", "exit code 137", etc.
  if (
    lowerMessage.includes("pod failed") ||
    /exit code[:\s]+[1-9]\d*/.test(lowerMessage) ||
    lowerMessage.includes("terminated with error") ||
    lowerMessage.includes("oomkilled") ||
    lowerMessage.includes("oom killed")
  ) {
    return K8sIssueTypes.JobFailed;
  }

  // Check for PVC capacity issues
  if (
    lowerMessage.includes("pvc capacity") ||
    lowerMessage.includes("pvc usage")
  ) {
    if (lowerMessage.includes("critical")) {
      return K8sIssueTypes.PvcCapacityCritical;
    }
    return K8sIssueTypes.PvcCapacityWarning;
  }

  // Check for PVC inode issues
  if (lowerMessage.includes("pvc inode")) {
    if (lowerMessage.includes("critical")) {
      return K8sIssueTypes.PvcInodeCritical;
    }
    return K8sIssueTypes.PvcInodeWarning;
  }

  return K8sIssueTypes.Unknown;
}

/**
 * Input type for creating K8s fingerprint params from unbranded strings.
 * Use this at system boundaries where data comes from external sources
 * (K8s API, database, etc.) that don't use branded types.
 *
 * Accepts both branded and unbranded strings for flexibility at boundaries.
 */
export type K8sFingerprintInput = {
  controllerKind?:
    | ControllerKind
    | string
    | null
    | undefined;
  controllerName?:
    | ControllerName
    | string
    | null
    | undefined;
  kubeSystemUid: KubeSystemUid | string;
  message: string;
  namespace: NamespaceName | string;
  resource: string;
  source: K8sFingerprintSource;
};

/**
 * Create K8sFingerprintParams from unbranded input at system boundaries.
 *
 * This helper centralizes the casting from unbranded strings (from K8s API, DB)
 * to branded types, making it explicit where type boundaries are crossed.
 *
 * @example
 * // From K8s issue data (unbranded strings)
 * const params = createK8sFingerprintParams({
 *   kubeSystemUid: "647e9b2f-95fa-4544-a779-e5d8bd77a63d",
 *   namespace: "vivus-prod",
 *   resource: "api-server-787cf77cdc-w6glv",
 *   controllerName: "api-server-787cf77cdc",
 *   controllerKind: "ReplicaSet",
 *   message: "CPU usage above threshold",
 * });
 * const fingerprint = generateK8sFingerprint(params);
 */
export function createK8sFingerprintParams(
  input: K8sFingerprintInput
): K8sFingerprintParams {
  return {
    issueType: detectK8sIssueType(input.message),
    kubeSystemUid: input.kubeSystemUid as KubeSystemUid,
    namespace: input.namespace as NamespaceName,
    resource: input.resource,
    source: input.source,
    ...(input.controllerKind !== undefined
      ? {
          controllerKind:
            input.controllerKind as ControllerKind | null,
        }
      : {}),
    ...(input.controllerName !== undefined
      ? {
          controllerName:
            input.controllerName as ControllerName | null,
        }
      : {}),
  };
}

/**
 * Create a fingerprint prefix for DB queries.
 * Useful for finding all issues from a specific source/sourceId.
 *
 * @example
 * createFingerprintPrefix("k8s", "clstr_abc")
 * // Returns: "k8s:clstr_abc:%"
 */
export function createFingerprintPrefix(
  source: FingerprintSource,
  sourceId: string
): FingerprintPrefix {
  return FingerprintPrefix.make(`${source}:${sourceId}:%`);
}

export function generateFingerprintsForIssues(
  kubeSystemUid: string,
  source: K8sFingerprintSource,
  issues: Array<{
    issueType?: string;
    message: string;
    namespace: string;
    resource: string;
  }>
): Array<{
  fingerprint: IssueFingerprint;
  issue: (typeof issues)[0];
}> {
  return issues.map((issue) => {
    const issueType =
      (issue.issueType as K8sIssueType) ??
      detectK8sIssueType(issue.message);

    return {
      fingerprint: generateK8sFingerprint({
        issueType,
        kubeSystemUid: kubeSystemUid as KubeSystemUid,
        namespace: issue.namespace as NamespaceName,
        resource: issue.resource,
        source,
      }) as string,
      issue,
    };
  });
}
